import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '~app/auth/auth.service';
import { InfluxFilters } from '../influx.service';
import { InfluxMetadataService, Scalar, ScalarObject } from './influx-metadata.service';
import { environment } from '~environments/environment';
import { IAnalysis, IDistinctWithLabelResults, ITimeLineResults, Tv2l } from '~app/v2l/v2l.type';

@Injectable()
export class InfluxMetadataV2lService extends InfluxMetadataService {

  constructor(
    private readonly http: HttpClient,
    private readonly auth: AuthService,
  ) {
    super();
  }

  protected get specialFields(): Map<string, Map<Scalar, Scalar>> {
    return;
  }

  protected load(field: string, filters: ScalarObject): Promise<ScalarObject[]> {
    const url = new URL(`v2l-api/v2l-egress/distinct/${field}`, environment.apigwURL);
    return (this.http.get(url.toString(), {params: this.dateRange(filters)}).toPromise()).then((data: IDistinctWithLabelResults) => {
      return data.data
        .filter((item) => item)
        //// using computed property name because of differences between the nature of the data
        .map( item => ({[field]: item}));
    }) as Promise<ScalarObject[]>;
  }

  protected async loadTimeline(
    bucket: string,
    measurement: string,
    filters: InfluxFilters = {},
  ): Promise<[string, number | string | boolean, string?][]> {
    //// no windowing with V2L
    delete filters._window;
    delete filters._windowFunction;
    delete filters._windowOffset;
    //// sort by default with "NaytteenottoHetki"
    const url = new URL(`v2l-api/v2l-egress/filter?${this.dateRange(filters)}&sort=NaytteenottoMatka.Naytteenotto.NaytteenottoHetki`, environment.apigwURL);
    const results:ITimeLineResults = await this.http.get(url.toString()).toPromise() as ITimeLineResults;
    return results.data
      .map((item: Tv2l) => item.NaytteenottoMatka.Naytteenotto.Nayte.Analyysitulos
        .filter((tulos:IAnalysis) => tulos.MaaritysmenetelmaNimi === filters['NaytteenottoMatka.Naytteenotto.Nayte.Analyysitulos.MaaritysmenetelmaNimi'])
        .map((distinct:IAnalysis) =>
          [
            `${item.NaytteenottoMatka.Naytteenotto.NaytteenottoHetki}`,
            distinct.ArvoLuku,
            `${item.NaytteenottoMatka.Naytteenotto.Nayte.NayteYmparisto??''} ${item.NaytteenottoMatka.Naytteenotto.Nayte.NayteNimi??''}`
          ]) as [string,number,string][]
      )
      .flat(1);
  }

  protected async distinctValues(field: string, filters: ScalarObject, asUnits: boolean = false): Promise<Set<Scalar>> {
    const data = await this.load(field, filters);
    //// engineering units
    if (asUnits) {
      return new Set(
        data
          .filter(row => row && row[field]['MaaritysmenetelmaNimi'] === filters['NaytteenottoMatka.Naytteenotto.Nayte.Analyysitulos.MaaritysmenetelmaNimi'])
          .map(row => row[field]['YksikkoKoodi'] ? row[field]['YksikkoKoodi'] : filters['NaytteenottoMatka.Naytteenotto.Nayte.Analyysitulos.MaaritysmenetelmaNimi'])
      );
    }
    //// filter values
    else {
      return new Set(
        data
          .filter(row => row)
          .map(row => row[field])
      );
    }

  }

  private dateRange(filters: ScalarObject): HttpParams {
    const initialDate = new Date();
    initialDate.setMonth(initialDate.getMonth() -12);
    const daterange = {daterange: `${filters._start ?? initialDate.toISOString()}|${filters._stop ?? new Date().toISOString()}`};
    const queryParams = {...daterange};
    for (const entry of Object.entries(filters)) {
      if (entry[0]!=='_start'&&entry[0]!=='_stop') {
        queryParams[entry[0]] = entry[1];
      }
    }
    return new HttpParams({fromObject: queryParams});
  }
}

